import { Phone, MessageSquare } from 'lucide-react';
import { QuickViewSheetLayout } from '@/components/ui/QuickViewSheetLayout';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useLead } from '@/hooks/useLeads';
import { LeadDetailContent } from './LeadDetailContent';

interface LeadQuickViewSheetProps {
  leadId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const STATUS_BADGES: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
  new: { label: 'New', variant: 'info' },
  processing: { label: 'Processing', variant: 'warning' },
  needs_follow_up: { label: 'Follow-up', variant: 'warning' },
  estimate_sent: { label: 'Estimate sent', variant: 'primary-soft' },
  estimate_accepted: { label: 'Accepted', variant: 'success' },
  converted: { label: 'Booked', variant: 'success' },
  lost: { label: 'Lost', variant: 'destructive-soft' },
};

export function LeadQuickViewSheet({
  leadId,
  open,
  onOpenChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: LeadQuickViewSheetProps) {
  const { data: lead, isLoading, error } = useLead(leadId);

  const handleClose = () => {
    onOpenChange(false);
  };

  const statusBadge = lead
    ? STATUS_BADGES[lead.status] ?? {
        label: lead.status.replace(/_/g, ' '),
        variant: 'primary-soft' as BadgeProps['variant'],
      }
    : null;

  const subtitle = lead
    ? [lead.lead_number, lead.service_types?.name, lead.customer_suburb].filter(Boolean).join(' · ')
    : undefined;

  return (
    <QuickViewSheetLayout
      open={open}
      onOpenChange={onOpenChange}
      size="640"
      title={lead?.customer_name ?? ''}
      subtitle={subtitle}
      badges={
        statusBadge && (
          <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
        )
      }
      actions={
        lead && (
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" className="h-8 gap-1.5">
              <a href={`tel:${lead.customer_phone}`}>
                <Phone className="h-3.5 w-3.5" />
                Call
              </a>
            </Button>
            <Button asChild variant="outline" size="sm" className="h-8 gap-1.5">
              <a href={`sms:${lead.customer_phone}`}>
                <MessageSquare className="h-3.5 w-3.5" />
                SMS
              </a>
            </Button>
          </div>
        )
      }
      isLoading={isLoading}
      error={error as Error | null}
      isEmpty={!lead && !isLoading && !error}
      emptyMessage="Lead not found"
      fullDetailPath={`/leads/${leadId}`}
      fullDetailLabel="Open full details"
      onPrevious={onPrevious}
      onNext={onNext}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
    >
      {lead && (
        <LeadDetailContent
          lead={lead}
          isSheet={true}
          onDeleted={handleClose}
          onClose={handleClose}
        />
      )}
    </QuickViewSheetLayout>
  );
}
